import type { ZodError } from 'zod';

/** Kody błędów aplikacji (docs/CONVENTIONS.md §5). Wspólne dla UI, REST, tRPC, MCP i AI. */
export const APP_ERROR_CODES = [
  'VALIDATION_ERROR',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'APPROVAL_REQUIRED',
  'STEP_UP_REQUIRED',
  'RATE_LIMITED',
  'INTEGRATION_UNAVAILABLE',
  'INTERNAL',
] as const;

export type AppErrorCode = (typeof APP_ERROR_CODES)[number];

/** Kod HTTP dla REST (docs/API_AUTH.md). */
export const HTTP_STATUS_BY_CODE: Record<AppErrorCode, number> = {
  VALIDATION_ERROR: 400,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  APPROVAL_REQUIRED: 412,
  STEP_UP_REQUIRED: 403,
  RATE_LIMITED: 429,
  INTEGRATION_UNAVAILABLE: 503,
  INTERNAL: 500,
};

/** Kod tRPC (TRPCError.code). `appCode` jedzie dodatkowo w `error.data.appCode`. */
export const TRPC_CODE_BY_CODE = {
  VALIDATION_ERROR: 'BAD_REQUEST',
  UNAUTHENTICATED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  APPROVAL_REQUIRED: 'PRECONDITION_FAILED',
  STEP_UP_REQUIRED: 'FORBIDDEN',
  RATE_LIMITED: 'TOO_MANY_REQUESTS',
  INTEGRATION_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  INTERNAL: 'INTERNAL_SERVER_ERROR',
} as const satisfies Record<AppErrorCode, string>;

/** Domyślne komunikaty PL (krótkie, bez danych wrażliwych). */
export const DEFAULT_ERROR_MESSAGES: Record<AppErrorCode, string> = {
  VALIDATION_ERROR: 'Nieprawidłowe dane.',
  UNAUTHENTICATED: 'Zaloguj się, aby kontynuować.',
  FORBIDDEN: 'Brak dostępu.',
  NOT_FOUND: 'Nie znaleziono.',
  CONFLICT: 'Rekord został zmieniony w międzyczasie. Odśwież i spróbuj ponownie.',
  APPROVAL_REQUIRED: 'Ta akcja wymaga akceptacji.',
  STEP_UP_REQUIRED: 'Potwierdź hasłem, aby kontynuować.',
  RATE_LIMITED: 'Zbyt wiele prób. Spróbuj ponownie później.',
  INTEGRATION_UNAVAILABLE: 'Integracja jest chwilowo niedostępna.',
  INTERNAL: 'Coś poszło nie tak. Spróbuj ponownie.',
};

export interface ValidationIssue {
  path: string;
  message: string;
}

export interface AppErrorBody {
  error: { code: AppErrorCode; message: string; details?: unknown };
}

export class AppError extends Error {
  readonly code: AppErrorCode;
  readonly details?: unknown;

  constructor(code: AppErrorCode, message?: string, options?: { details?: unknown; cause?: unknown }) {
    super(message ?? DEFAULT_ERROR_MESSAGES[code], options?.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'AppError';
    this.code = code;
    if (options?.details !== undefined) this.details = options.details;
  }

  get httpStatus(): number {
    return HTTP_STATUS_BY_CODE[this.code];
  }

  toBody(): AppErrorBody {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.details === undefined ? {} : { details: this.details }),
      },
    };
  }
}

export function isAppError(err: unknown): err is AppError {
  return (
    err instanceof AppError ||
    (typeof err === 'object' && err !== null && (err as { name?: unknown }).name === 'AppError')
  );
}

export function isAppErrorCode(value: unknown): value is AppErrorCode {
  return typeof value === 'string' && (APP_ERROR_CODES as readonly string[]).includes(value);
}

/** Zamienia ZodError na VALIDATION_ERROR z listą `{ path, message }` w `details.issues`. */
export function fromZodError(err: ZodError, message = DEFAULT_ERROR_MESSAGES.VALIDATION_ERROR): AppError {
  const issues: ValidationIssue[] = err.issues.map((i) => ({ path: i.path.join('.'), message: i.message }));
  return new AppError('VALIDATION_ERROR', message, { details: { issues } });
}

/** Każdy nieznany błąd → INTERNAL (bez szczegółów dla klienta). */
export function toAppError(err: unknown): AppError {
  if (isAppError(err)) return err;
  return new AppError('INTERNAL', undefined, { cause: err });
}
